'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { useRouter } from 'next/navigation'
import { useLanguage } from '@/context/LanguageContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}

export default function AboutPage() {
  const { t } = useLanguage()
  const router = useRouter()

  return (
    <motion.div
      className="mx-auto max-w-4xl space-y-10 pb-12"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="py-6 text-center">
        <h1 className="text-display-3 font-bold text-brand-navy lg:text-display-2">
          {t('about.hero.headline')}
        </h1>
        <p className="text-display-5 mt-2 font-semibold text-gradient">
          {t('about.hero.subtitle')}
        </p>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {t('about.hero.description')}
        </p>
      </motion.div>

      <motion.div variants={itemVariants} className="grid gap-4 sm:grid-cols-3">
        <Card className="border-red-100 bg-red-50/10">
          <CardContent className="p-6 text-center">
            <h3 className="text-display-5 font-bold text-red-600">
              {t('about.stats.affected.val')}
            </h3>
            <p className="text-micro mt-2 font-semibold uppercase tracking-wide text-brand-muted">
              {t('about.stats.affected.label')}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {t('about.stats.affected.desc')}
            </p>
          </CardContent>
        </Card>
        <Card className="border-red-100 bg-red-50/10">
          <CardContent className="p-6 text-center">
            <h3 className="text-display-5 font-bold text-red-600">
              {t('about.stats.mortality.val')}
            </h3>
            <p className="text-micro mt-2 font-semibold uppercase tracking-wide text-brand-muted">
              {t('about.stats.mortality.label')}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {t('about.stats.mortality.desc')}
            </p>
          </CardContent>
        </Card>
        <Card className="border-emerald-100 bg-emerald-50/10">
          <CardContent className="p-6 text-center">
            <h3 className="text-display-5 font-bold text-emerald-600">
              {t('about.stats.preventable.val')}
            </h3>
            <p className="text-micro mt-2 font-semibold uppercase tracking-wide text-brand-muted">
              {t('about.stats.preventable.label')}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
              {t('about.stats.preventable.desc')}
            </p>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={itemVariants} className="space-y-4">
        <h2 className="text-display-5 font-bold text-brand-navy">
          {t('about.impact.title')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="flex items-center gap-2 font-semibold text-brand-navy">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              {t('about.impact.stunted.title')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('about.impact.stunted.desc')}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="flex items-center gap-2 font-semibold text-brand-navy">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              {t('about.impact.immunity.title')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('about.impact.immunity.desc')}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="flex items-center gap-2 font-semibold text-brand-navy">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              {t('about.impact.cognitive.title')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('about.impact.cognitive.desc')}
            </p>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="flex items-center gap-2 font-semibold text-brand-navy">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              {t('about.impact.economic.title')}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('about.impact.economic.desc')}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="space-y-4">
        <h2 className="text-display-5 font-bold text-brand-navy">
          {t('about.pillars.title')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="relative overflow-hidden">
            <CardWatermark opacity={2} scale={0.7} />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-brand-navy">
                {t('about.pillars.p1.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs leading-relaxed text-muted-foreground">
              {t('about.pillars.p1.desc')}
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <CardWatermark opacity={2} scale={0.7} />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-brand-navy">
                {t('about.pillars.p2.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs leading-relaxed text-muted-foreground">
              {t('about.pillars.p2.desc')}
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden">
            <CardWatermark opacity={2} scale={0.7} />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-brand-navy">
                {t('about.pillars.p3.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs leading-relaxed text-muted-foreground">
              {t('about.pillars.p3.desc')}
            </CardContent>
          </Card>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} className="pt-6 text-center">
        <Button variant="gradient" size="lg" onClick={() => router.push('/')}>
          <Icons.sparkles className="mr-2 h-5 w-5" />
          {t('about.cta.btn')}
        </Button>
      </motion.div>
    </motion.div>
  )
}
